import { createSvg, clamp, NS } from "./helpers";


const GRAY = "#b8bdc1";
const PINK = "#e80c89";
const SCALE = 4;
const EASE_POWER = 3;

function range(count) {
    return Array.from({ length: count }, (_, index) => index);
}

function ease(progress) {
    return 1 - Math.pow(1 - progress, EASE_POWER);
}

function stateFromBase(basePoints, pinkIndices, opacityMap = {}) {
    const pink = new Set(pinkIndices);
    return basePoints.map((point, index) => ({
        ...point,
        fill: pink.has(index) ? PINK : GRAY,
        opacity: pink.has(index) ? (opacityMap[index] ?? 1) : (point.opacity ?? 1),
    }));
}

function createFrameRenderer(states, options = {}) {
    const svg = createSvg();
    const scaleLayer = document.createElementNS(NS, "g");
    scaleLayer.setAttribute("transform", `scale(${SCALE})`);
    svg.appendChild(scaleLayer);

    const layer = document.createElementNS(NS, "g");
    if (options.groupTransform) {
        layer.setAttribute("transform", options.groupTransform);
    }
    scaleLayer.appendChild(layer);

    const maxDots = Math.max(...states.map((state) => state.length));
    const circles = Array.from({ length: maxDots }, () => {
        const circle = document.createElementNS(NS, "circle");
        circle.classList.add("animatedVisualSwap__vectorDot");
        circle.setAttribute("cx", "0");
        circle.setAttribute("cy", "0");
        circle.setAttribute("r", "0");
        layer.appendChild(circle);
        return circle;
    });

    return {
        node: svg,
        apply(progress) {
            const t = ease(clamp(progress, 0, 1));
            const frameIndex = Math.round(t * (states.length - 1));
            const frame = states[frameIndex];

            circles.forEach((circle, dotIndex) => {
                const point = frame[dotIndex];
                if (!point) {
                    circle.setAttribute("r", "0");
                    circle.setAttribute("opacity", "0");
                    return;
                }

                circle.setAttribute("cx", String(point.x));
                circle.setAttribute("cy", String(point.y));
                circle.setAttribute("r", String(point.r ?? 2));
                circle.setAttribute("fill", point.fill ?? GRAY);
                circle.setAttribute("opacity", String(point.opacity ?? 1));
            });
        },
    };
}

const quantitativeBase = [
    { x: 16, y: 16, r: 3 },
    { x: 32, y: 16, r: 3 },
    { x: 48, y: 16, r: 3 },
    { x: 64, y: 16, r: 3 },
    { x: 16, y: 32, r: 3 },
    { x: 32, y: 32, r: 3 },
    { x: 48, y: 32, r: 3 },
    { x: 64, y: 32, r: 3 },
    { x: 16, y: 48, r: 3 },
    { x: 32, y: 48, r: 3 },
    { x: 48, y: 48, r: 3 },
    { x: 64, y: 48, r: 3 },
    { x: 16, y: 64, r: 3 },
    { x: 32, y: 64, r: 3 },
    { x: 48, y: 64, r: 3 },
    { x: 64, y: 64, r: 3 },
];
const quantitativeStates = [
    stateFromBase(quantitativeBase, []),
    stateFromBase(quantitativeBase, [0]),
    stateFromBase(quantitativeBase, [0, 1]),
    stateFromBase(quantitativeBase, [0, 1, 2]),
    stateFromBase(quantitativeBase, [0, 1, 2, 4, 5]),
    stateFromBase(quantitativeBase, [0, 1, 2, 4, 5, 6, 8, 9]),
    stateFromBase(quantitativeBase, [0, 1, 2, 3, 4, 5, 6, 8, 9, 10]),
    stateFromBase(quantitativeBase, range(12)),
    stateFromBase(quantitativeBase, range(16)),
];

const qualitativeBase = [
    { x: 51, y: 65, r: 3 },
    { x: 40, y: 53, r: 3 },
    { x: 40, y: 67, r: 3 },
    { x: 29, y: 65, r: 3 },
    { x: 67, y: 40, r: 3 },
    { x: 50, y: 50, r: 3 },
    { x: 30, y: 50, r: 3 },
    { x: 15, y: 30, r: 3 },
    { x: 15, y: 50, r: 3 },
    { x: 20, y: 59, r: 3 },
    { x: 53, y: 40, r: 3 },
    { x: 40, y: 40, r: 3 },
    { x: 30, y: 30, r: 3 },
    { x: 27, y: 40, r: 3 },
    { x: 65, y: 30, r: 3 },
    { x: 60, y: 21, r: 3 },
    { x: 20, y: 21, r: 3 },
    { x: 65, y: 50, r: 3 },
    { x: 60, y: 59, r: 3 },
    { x: 40, y: 27, r: 3 },
    { x: 50, y: 30, r: 3 },
    { x: 40, y: 13, r: 3 },
    { x: 29, y: 15, r: 3 },
    { x: 51, y: 15, r: 3 },
    { x: 13, y: 40, r: 3 },
];
const qualitativeStates = [
    stateFromBase(qualitativeBase, []),
    stateFromBase(qualitativeBase, [21]),
    stateFromBase(qualitativeBase, [21, 22, 23]),
    stateFromBase(qualitativeBase, [21, 22, 23, 19, 14, 12, 17, 18]),
    stateFromBase(qualitativeBase, [21, 22, 23, 19, 14, 12, 17, 18, 24, 13, 11, 7, 4]),
    stateFromBase(qualitativeBase, [21, 22, 23, 19, 14, 12, 17, 18, 24, 13, 11, 7, 4, 9, 10, 8]),
    stateFromBase(qualitativeBase, [21, 22, 23, 19, 14, 12, 17, 18, 24, 13, 11, 7, 4, 9, 10, 8, 20, 6, 5, 15]),
    stateFromBase(qualitativeBase, [21, 22, 23, 19, 14, 12, 17, 18, 24, 13, 11, 7, 4, 9, 10, 8, 20, 6, 5, 15, 0, 1, 16, 3]),
    stateFromBase(qualitativeBase, range(qualitativeBase.length)),
];

const analysisBase = [
    { x: 50, y: 50, r: 1 },
    { x: 34, y: 50, r: 2 }, { x: 18, y: 50, r: 2 }, { x: 2, y: 50, r: 1 },
    { x: 50, y: 34, r: 2 }, { x: 34, y: 34, r: 3 }, { x: 18, y: 34, r: 3 }, { x: 2, y: 34, r: 2 },
    { x: 50, y: 18, r: 2 }, { x: 33.9998, y: 18.0001, r: 3 }, { x: 25.8284, y: 26.1715, r: 3 }, { x: 18, y: 18, r: 3 }, { x: 2, y: 18, r: 2 },
    { x: 50, y: 2, r: 1 }, { x: 34, y: 2, r: 2 }, { x: 18, y: 2, r: 2 }, { x: 2, y: 2, r: 1 },
];
const analysisStates = [
    stateFromBase(analysisBase, []),
    stateFromBase(analysisBase, [16]),
    stateFromBase(analysisBase, [11, 12, 15, 16]),
    stateFromBase(analysisBase, [7, 10, 11, 12, 14, 15, 16]),
    stateFromBase(analysisBase, [3, 6, 7, 9, 10, 11, 12, 13, 14, 15, 16]),
    stateFromBase(analysisBase, [2, 3, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]),
    stateFromBase(analysisBase, [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 14]),
    stateFromBase(analysisBase, [0, 1, 2, 4, 5, 8, 10]),
    stateFromBase(analysisBase, [0, 1, 4, 10]),
    stateFromBase(analysisBase, [10]),
];

const experimentBase = [
    { x: 40, y: 13, r: 3 },
    { x: 31, y: 30, r: 3 }, { x: 40, y: 30, r: 3 }, { x: 49, y: 30, r: 3 },
    { x: 22, y: 47, r: 3 }, { x: 31, y: 47, r: 3 }, { x: 40, y: 47, r: 3 }, { x: 49, y: 47, r: 3 }, { x: 58, y: 47, r: 3 },
    { x: 13, y: 64, r: 3 }, { x: 22, y: 64, r: 3 }, { x: 31, y: 64, r: 3 }, { x: 40, y: 64, r: 3 }, { x: 49, y: 64, r: 3 }, { x: 58, y: 64, r: 3 }, { x: 67, y: 64, r: 3 },
];
const experimentStates = [
    stateFromBase(experimentBase, []),
    stateFromBase(experimentBase, [11, 12], { 11: 0.56, 12: 1 }),
    stateFromBase(experimentBase, [5, 6, 10, 11, 12, 13], { 5: 0.56, 6: 0.72, 10: 0.56, 11: 0.9, 12: 1, 13: 0.56 }),
    stateFromBase(experimentBase, [1, 2, 4, 5, 6, 7, 9, 10, 11, 12, 13, 14], { 1: 0.56, 2: 0.72, 4: 0.56, 5: 0.9, 6: 1, 7: 0.56, 9: 0.56, 10: 0.9, 11: 1, 12: 1, 13: 1, 14: 0.56 }),
    stateFromBase(experimentBase, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], { 0: 0.72, 1: 0.9, 2: 1, 3: 0.72, 4: 0.9, 5: 1, 6: 1, 7: 1, 8: 0.72, 9: 0.9, 10: 1, 11: 1, 12: 1, 13: 1, 14: 1, 15: 0.72 }),
    stateFromBase(experimentBase, range(experimentBase.length)),
];

const officeBase = [
    { x: 50.87, y: 39.56, r: 3 },
    { x: 39.56, y: 50.87, r: 3 },
    { x: 39.56, y: 28.24, r: 3 },
    { x: 39.56, y: 39.8, r: 3 },
    { x: 28.24, y: 39.56, r: 3 },
    { x: 29.83, y: 17.83, r: 2 },
    { x: 48.83, y: 17.83, r: 2 },
    { x: 48.83, y: 61.83, r: 2 },
    { x: 59.83, y: 56.83, r: 2 },
    { x: 18.83, y: 56.83, r: 2 },
    { x: 29.83, y: 61.83, r: 2 },
    { x: 13.83, y: 34.83, r: 2 },
    { x: 13.83, y: 44.83, r: 2 },
    { x: 64.83, y: 34.83, r: 2 },
    { x: 64.83, y: 44.83, r: 2 },
    { x: 18.83, y: 23.83, r: 2 },
    { x: 59.83, y: 23.83, r: 2 },
    { x: 39.42, y: 12.42, r: 1 },
    { x: 13.42, y: 27.42, r: 1 },
    { x: 65.42, y: 27.42, r: 1 },
    { x: 65.42, y: 53.42, r: 1 },
    { x: 13.42, y: 53.42, r: 1 },
    { x: 39.42, y: 67.42, r: 1 },
];
const officeStates = [
    stateFromBase(officeBase, []),
    stateFromBase(officeBase, [8, 9, 15, 16, 17, 18, 19, 20, 21, 22], {
        8: 0.22, 9: 0.56, 15: 0.56, 16: 0.22, 17: 0.56, 18: 0.56, 19: 0.56, 20: 0.56, 21: 0.56, 22: 0.56,
    }),
    stateFromBase(officeBase, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22], {
        5: 0.56, 6: 0.56, 7: 0.56, 8: 0.57, 9: 1, 10: 0.56, 11: 0.56, 12: 0.56, 13: 0.56, 14: 0.56,
        15: 1, 16: 0.56, 17: 1, 18: 1, 19: 1, 20: 1, 21: 1, 22: 1,
    }),
    stateFromBase(officeBase, [0, 1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 19, 20, 21], {
        0: 0.56, 1: 0.56, 2: 0.56, 4: 0.56, 5: 1, 6: 1, 7: 1, 8: 1, 9: 1, 10: 1, 11: 1, 12: 1, 13: 1, 14: 1,
        15: 1, 16: 1, 18: 1, 19: 0.84, 20: 0.84, 21: 1,
    }),
    stateFromBase(officeBase, [0, 1, 2, 3, 4], {
        0: 1, 1: 1, 2: 1, 3: 0.56, 4: 1,
    }),
    stateFromBase(officeBase, [3]),
];

const advancedBase = [
    { x: 39, y: 66, r: 1 },
    { x: 54, y: 27, r: 2 },
    { x: 46, y: 20, r: 2 },
    { x: 54, y: 54, r: 2 },
    { x: 46, y: 60, r: 2 },
    { x: 65, y: 40, r: 3 },
    { x: 39, y: 40, r: 2 },
    { x: 13, y: 40, r: 1 },
    { x: 52, y: 40, r: 3 },
    { x: 25, y: 40, r: 2 },
    { x: 39, y: 14, r: 1 },
];
const advancedStates = [
    stateFromBase(advancedBase, []),
    stateFromBase(advancedBase, [7], { 7: 0.56 }),
    stateFromBase(advancedBase, [7, 9], { 7: 0.84, 9: 0.56 }),
    stateFromBase(advancedBase, [6, 7, 9], { 6: 0.56, 7: 0.84, 9: 1 }),
    stateFromBase(advancedBase, [6, 7, 8, 9], { 6: 1, 7: 0.84, 8: 0.56, 9: 1 }),
    stateFromBase(advancedBase, [5, 6, 7, 8, 9], { 5: 0.56, 6: 1, 7: 0.84, 8: 1, 9: 1 }),
    stateFromBase(advancedBase, [1, 3, 5, 6, 7, 8, 9], { 1: 0.56, 3: 0.56, 5: 1, 6: 1, 7: 0.84, 8: 1, 9: 1 }),
    stateFromBase(advancedBase, [1, 2, 3, 4, 5, 6, 7, 8, 9], { 1: 1, 2: 0.56, 3: 1, 4: 0.56, 5: 1, 6: 1, 7: 0.84, 8: 1, 9: 1 }),
    stateFromBase(advancedBase, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10], { 0: 0.56, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1, 7: 0.84, 8: 1, 9: 1, 10: 0.56 }),
    stateFromBase(advancedBase, [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10], { 0: 0.84, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1, 7: 0.84, 8: 1, 9: 1, 10: 0.84 }),
];

export function renderMethodologyQuantitative() {
    return createFrameRenderer(quantitativeStates);
}

export function renderMethodologyQualitative() {
    return createFrameRenderer(qualitativeStates);
}

export function renderMethodologyAnalysis() {
    return createFrameRenderer(analysisStates, {
        groupTransform: "translate(14 14) rotate(-45 26 26)",
    });
}

export function renderMethodologyExperiment() {
    return createFrameRenderer(experimentStates);
}

export function renderMethodologyOffice() {
    return createFrameRenderer(officeStates);
}

export function renderMethodologyAdvanced() {
    return createFrameRenderer(advancedStates);
}
